/**
 * Stores > HQ photos review at /stores/hq/photos.
 *
 * Browses the merchandising photos stores attach to their Today check-ins.
 * Date picker (defaults today Denver) with prev/next arrows; photos are
 * grouped by store. Clicking any photo opens a full-size lightbox overlay
 * (Escape or clicking the backdrop closes it, no library dependency).
 *
 * Gated by stores:hq. 404/503 -> not-available placeholder.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import { formatInstant } from '@ampersandbrands/ui';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useHqPhotos,
  isNotAvailable,
  denverToday,
  type HqPhoto,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  NotAvailableCard,
  ErrorCard,
  secondaryBtnCls,
} from './storesShared';

function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatTime(iso: string): string {
  return formatInstant(iso, { timeOnly: true });
}

function groupByStore(photos: HqPhoto[]): { store_no: number; store_name: string; photos: HqPhoto[] }[] {
  const map = new Map<number, { store_no: number; store_name: string; photos: HqPhoto[] }>();
  for (const p of photos) {
    let entry = map.get(p.store_no);
    if (!entry) {
      entry = { store_no: p.store_no, store_name: p.store_name, photos: [] };
      map.set(p.store_no, entry);
    }
    entry.photos.push(p);
  }
  return [...map.values()].sort((a, b) => a.store_name.localeCompare(b.store_name));
}

function Lightbox({ photo, onClose }: { photo: HqPhoto; onClose: () => void }) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="flex max-h-[90dvh] max-w-[90dvw] flex-col items-center gap-2">
        <img
          src={photo.url}
          alt={photo.caption ?? `Photo from ${photo.store_name}`}
          className="max-h-[80dvh] max-w-[90dvw] rounded object-contain"
        />
        {(photo.caption || photo.store_name) && (
          <div className="text-center">
            <p className="text-sm font-medium text-white">{photo.store_name}</p>
            {photo.caption && (
              <p className="text-xs text-white/80">{photo.caption}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function HqPhotos() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.photos', user?.superAdmin);

  const [date, setDate] = useState<string>(denverToday());
  const [lightbox, setLightbox] = useState<HqPhoto | null>(null);

  const photosQ = useHqPhotos(date);

  const today = denverToday();
  const isToday = date === today;

  const handlePrev = useCallback(() => setDate((d) => shiftDate(d, -1)), []);
  const handleNext = useCallback(() => {
    if (!isToday) setDate((d) => shiftDate(d, 1));
  }, [isToday]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  if (user && !canView) return <AccessDeniedCard what="Store photos" />;
  if (isNotAvailable(photosQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <StoresHeader
          title="Store photos"
          actions={<ToolPinToggle toolSlug="stores-hq-photos" toolName="Store photos" />}
        />
        <NotAvailableCard what="Store photos" />
      </div>
    );
  }

  const groups = groupByStore(photosQ.data?.photos ?? []);
  const totalPhotos = photosQ.data?.photos.length ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <StoresHeader
        title="Store photos"
        subtitle="Merchandising photos from store daily check-ins."
        actions={<ToolPinToggle toolSlug="stores-hq-photos" toolName="Store photos" />}
      />

      {/* Date navigation */}
      <div className="mb-5 flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="photo-date" className="mb-1 block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Date
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrev}
              className={secondaryBtnCls}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <input
              id="photo-date"
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value || today)}
              className="rounded border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
            <button
              type="button"
              onClick={handleNext}
              disabled={isToday}
              className={secondaryBtnCls}
              aria-label="Next day"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {!photosQ.isLoading && (
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted pb-1.5">
            {totalPhotos === 0 ? 'No photos' : `${totalPhotos} photo${totalPhotos === 1 ? '' : 's'} from ${groups.length} store${groups.length === 1 ? '' : 's'}`}
          </p>
        )}
      </div>

      {/* Content area */}
      {photosQ.isLoading && (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading photos...</p>
      )}

      {photosQ.error && !isNotAvailable(photosQ.error) && (
        <ErrorCard message={photosQ.error.message} />
      )}

      {!photosQ.isLoading && !photosQ.error && groups.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-amp-line bg-amp-cream-warm px-4 py-12 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <Camera className="mb-3 h-8 w-8 text-amp-ink-muted dark:text-amp-dark-ink-muted" />
          <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">No photos on this day</p>
          <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Stores that submitted check-ins with photos will appear here.
          </p>
        </div>
      )}

      {!photosQ.isLoading && groups.length > 0 && (
        <div className="min-h-0 space-y-8 overflow-y-auto">
          {groups.map((group) => (
            <section key={group.store_no}>
              <h2 className="mb-3 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
                {group.store_name}
                <span className="ml-1.5 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  #{group.store_no}
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {group.photos.map((photo) => (
                  <button
                    key={photo.photo_id}
                    type="button"
                    onClick={() => setLightbox(photo)}
                    className="group rounded-lg border border-amp-line bg-amp-white text-left transition-shadow hover:shadow-1 dark:border-amp-dark-line dark:bg-amp-dark-surface"
                  >
                    <div className="aspect-square overflow-hidden rounded-t-lg bg-amp-cream-warm dark:bg-amp-dark-surface-warm">
                      <img
                        src={photo.url}
                        alt={photo.caption ?? `Photo from ${group.store_name}`}
                        className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-2">
                      {photo.caption && (
                        <p className="truncate text-[12px] text-amp-ink dark:text-amp-dark-ink">
                          {photo.caption}
                        </p>
                      )}
                      <p className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {formatTime(photo.created_at)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {lightbox && <Lightbox photo={lightbox} onClose={closeLightbox} />}
    </div>
  );
}
